import { useEffect, useState } from "react";

const STORAGE_KEY = "plotlounge:cockpit:lastVisitedClubs";

interface VisitMap {
  [clubId: string]: string;
}

function read(): VisitMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as VisitMap;
    }
    return {};
  } catch {
    return {};
  }
}

function write(map: VisitMap): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore — quota / private mode
  }
}

export function recordClubVisit(clubId: string): void {
  const map = read();
  map[clubId] = new Date().toISOString();
  write(map);
}

export function getLastVisitedClubId(
  organizerClubIds: readonly string[],
): string | null {
  const map = read();
  const entries = organizerClubIds
    .map((id) => [id, map[id]] as const)
    .filter((entry): entry is readonly [string, string] => Boolean(entry[1]))
    .sort((a, b) => b[1].localeCompare(a[1]));
  return entries[0]?.[0] ?? null;
}

export function useLastVisitedSorted<T extends { id: string }>(
  clubs: T[],
): T[] {
  const [order, setOrder] = useState<VisitMap>(() => read());
  useEffect(() => {
    setOrder(read());
  }, [clubs.length]);
  if (clubs.length <= 1) return clubs;
  return [...clubs].sort((a, b) => {
    const aTime = order[a.id] ?? "";
    const bTime = order[b.id] ?? "";
    if (aTime === bTime) return 0;
    return bTime.localeCompare(aTime);
  });
}

/**
 * Records `clubId` as last-visited on mount and on changes.
 * Use inside a `/clubs/:id`-scoped component once the club id is known.
 */
export function useRecordClubVisit(clubId: string | undefined): void {
  useEffect(() => {
    if (!clubId) return;
    recordClubVisit(clubId);
  }, [clubId]);
}
